import { gql } from '@apollo/client';

/**
 * Spec 0018 (career center), increment A — ERP-scope queries.
 *
 * Operation names are deliberately different from the backend field names
 * (`careerMembers`, `careerDirections`, `careerLessonsErp`) to avoid the
 * codegen result-type collapse.
 *
 * Wire-level enums are plain Strings: member `status` is
 * `active|paused|placed|archived`, lesson `completionKind` is
 * `acknowledge|artifact`. They are narrowed in the `core/hooks/CareerCenter`
 * model layer, never inline in components.
 */

export const CareerMembersListQuery = gql`
  query ErpCareerMembers($status: String, $directionId: ID, $search: String) {
    careerMembers(status: $status, directionId: $directionId, search: $search) {
      id
      studentId
      studentName
      studentEmail
      groupId
      groupName
      directionId
      directionTitle
      status
      enrolledAt
      notes
      lessonsCompleted
      lessonsTotal
    }
  }
`;

/**
 * Increment B1 — one member's full card, including the nested structured
 * `profile` the graduate builds in the LMS (nullable: a member may not have
 * started one). Operation name stays distinct from the `careerMemberDetail`
 * field so codegen keeps a dedicated result type. ERP reads this profile
 * read-only; editing lives in the LMS.
 */
export const CareerMemberDetailQuery = gql`
  query ErpCareerMemberDetail($id: ID!) {
    careerMemberDetail(id: $id) {
      id
      growth {
        xp
        level
        levelTitle
        medalsEarned
        medalsTotal
        trackTitle
        trackIsDefault
        nextCourses {
          courseId
          courseName
          courseUrl
          adds
          covered
          totalMissing
        }
        codes {
          id
          code
          status
          expiresAt
          campaignTitle
          discountKind
          discountValue
          currency
        }
      }
      studentName
      studentEmail
      profile {
        id
        firstName
        lastName
        email
        phone
        photoUrl
        city
        country
        relocationReady
        workMode
        workPermit
        availableFrom
        seniority
        desiredPosition
        desiredSalary
        githubUrl
        linkedinUrl
        portfolioUrl
        customSkills
        isSearchable
        contactsVisible
        completeness
        skills {
          skillId
          name
          category
          level
          years
        }
        projects {
          id
          title
          description
          role
          repoUrl
          demoUrl
          technologyNames
        }
        experience {
          id
          company
          position
          description
          startYear
          startMonth
          endYear
          endMonth
          isCurrent
        }
        education {
          id
          institution
          degree
          specialization
          startYear
          startMonth
          endYear
          endMonth
        }
        languages {
          id
          language
          level
        }
        certificates {
          id
          title
          issuer
          issuedAt
          url
          fileUrl
        }
        locales {
          language
          jobTitle
          about
        }
      }
    }
  }
`;

export const CareerDirectionsQuery = gql`
  query ErpCareerDirections($includeInactive: Boolean) {
    careerDirections(includeInactive: $includeInactive) {
      id
      slug
      title
      order
      isActive
    }
  }
`;

/**
 * The staff-side lesson list. Unlike the LMS `careerLessons`, this returns
 * unpublished lessons too and never nulls `content` — ERP edits the track.
 *
 * Spec 0020 (authoring parity) adds `videoUrl` and `attachments`: the media the
 * ERP lesson form now uploads. The `attachments` selection is kept
 * byte-for-byte identical to the one in the video/attachment mutations
 * (`mutations/careerCenter.ts`) so the query- and mutation-derived attachment
 * types stay structurally interchangeable in the model layer.
 *
 * Spec 0020 Фаза B/C also adds the staff completion funnel (`stats`, nullable —
 * the backend serves it only in ERP scope) and the self-check quiz
 * (`quizQuestions`, whose `correctIndexes` IS populated for ERP authors — the
 * LMS scope masks it). The `quizQuestions` selection matches the one returned by
 * `SaveCareerLessonQuiz` field-for-field so the two types are interchangeable.
 */
export const CareerLessonsErpQuery = gql`
  query ErpCareerLessons {
    careerLessonsErp {
      id
      order
      title
      summary
      content
      durationMin
      completionKind
      artifactHint
      outcomeKind
      outcomeLabel
      materialLanguage
      isPublished
      videoUrl
      attachments {
        id
        originalName
        contentType
        sizeBytes
        url
      }
      stats {
        openedCount
        completedCount
        membersTotal
      }
      quizQuestions {
        id
        order
        text
        options
        correctIndexes
      }
      exercise {
        id
        intro
        outro
        templateId
        templateTitle
        hasSubmissions
        fields {
          id
          number
          label
          helpText
          placeholder
          fieldType
          required
          options
          maxLength
          binding
          bindingLanguage
        }
      }
    }
  }
`;

/**
 * Spec 0020 Фаза B — one member's per-lesson learning map for the progress
 * drawer (`careerMemberProgress`, ERP scope + CAREER_ROLES). Op name stays
 * distinct from the field so codegen keeps a dedicated result type. Every field
 * is non-null except the two timestamps.
 */
export const CareerMemberProgressQuery = gql`
  query CareerMemberLearningProgress($memberId: ID!) {
    careerMemberProgress(memberId: $memberId) {
      lessonId
      order
      title
      isOpened
      openedAt
      openCount
      isCompleted
      completedAt
    }
  }
`;

/**
 * Increment B — the Вакансии tab (`careerVacanciesErp`). Unlike the LMS
 * `careerVacancies`, this returns every status (DRAFT included) so staff can
 * run the DRAFT→PUBLISHED workflow. Operation name stays distinct from the
 * `careerVacanciesErp` field to avoid the codegen result-type collapse.
 *
 * Wire-level enums are plain Strings (`status`, `workMode`, `seniority`,
 * `employmentType`) — narrowed in the `core/hooks/CareerCenter` model layer.
 */
export const VacanciesErpListQuery = gql`
  query ErpVacancies(
    $status: String
    $directionId: ID
    $search: String
    $createdFrom: Date
    $createdTo: Date
    $freshness: String
    $hideArchived: Boolean
  ) {
    careerVacanciesErp(
      status: $status
      directionId: $directionId
      search: $search
      createdFrom: $createdFrom
      createdTo: $createdTo
      freshness: $freshness
      hideArchived: $hideArchived
    ) {
      id
      title
      companyName
      applyUrl
      location
      workMode
      seniority
      employmentType
      salaryMin
      salaryMax
      salaryCurrency
      description
      status
      publishedAt
      expiresAt
      createdAt
      directionIds
      directionTitles
      skillIds
      skillNames
      appliedCount
      source
      createdByName
      reviewedByName
      reviewedAt
      updatedAt
      duplicateOf {
        vacancyId
        title
        companyName
        status
        score
      }
    }
  }
`;

/**
 * ТЗ 0035 — та же доска, но СТРАНИЦАМИ. `careerVacanciesErp` остаётся для
 * экранов, которым пагинация не нужна; доска ходит сюда.
 *
 * Курсор, а не смещение: ночной агент вставляет строки в ГОЛОВУ сортировки
 * ровно тогда, когда модератор листает очередь, а смещение при вставке в
 * голову показывает одну строку дважды и теряет другую.
 *
 * `source` фильтруется НА СЕРВЕРЕ. До 0035 он считался клиентом — при полном
 * списке это было безразлично, но под пагинацией клиентский фильтр стал бы не
 * медленным, а неверным: он отсеивал бы внутри уже полученной страницы.
 */
export const VacancyBoardQuery = gql`
  query ErpVacancyBoard(
    $status: String
    $directionId: ID
    $search: String
    $createdFrom: Date
    $createdTo: Date
    $freshness: String
    $hideArchived: Boolean
    $source: String
    $hasDuplicates: Boolean
    $orderBy: String
    $orderDir: String
    $first: Int
    $after: String
    $skillIds: [ID!]
  ) {
    careerVacancyBoard(
      status: $status
      directionId: $directionId
      search: $search
      createdFrom: $createdFrom
      createdTo: $createdTo
      freshness: $freshness
      hideArchived: $hideArchived
      source: $source
      hasDuplicates: $hasDuplicates
      orderBy: $orderBy
      orderDir: $orderDir
      first: $first
      after: $after
      skillIds: $skillIds
    ) {
      edges {
        cursor
        node {
          id
          title
          companyName
          applyUrl
          location
          workMode
          seniority
          employmentType
          salaryMin
          salaryMax
          salaryCurrency
          description
          status
          publishedAt
          expiresAt
          createdAt
          directionIds
          directionTitles
          skillIds
          skillNames
          appliedCount
          source
          createdByName
          reviewedByName
          reviewedAt
          updatedAt
          duplicateOf {
            vacancyId
            title
            companyName
            status
            score
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      totalCountCapped
    }
  }
`;

/** One vacancy by id (staff scope — any status). */
export const VacancyErpDetailQuery = gql`
  query ErpVacancy($id: ID!) {
    careerVacancyErp(id: $id) {
      id
      title
      companyName
      applyUrl
      location
      workMode
      seniority
      employmentType
      salaryMin
      salaryMax
      salaryCurrency
      description
      status
      publishedAt
      expiresAt
      createdAt
      directionIds
      directionTitles
      skillIds
      skillNames
      appliedCount
      source
      createdByName
      reviewedByName
      reviewedAt
      updatedAt
      duplicateOf {
        vacancyId
        title
        companyName
        status
        score
      }
    }
  }
`;

/**
 * Counters for the Дашборд tab: the funnel plus the state of the moderation
 * queue (ТЗ 0035).
 *
 * `vacanciesPublished` считается ТЕМ ЖЕ предикатом, что лента студента —
 * раньше он игнорировал срок и обещал вакансии, которых студент не видит.
 * Очередь черновиков вынесена сюда же: с тех пор как их заводит ночной агент,
 * «сколько ждёт проверки и сколько дней ждёт самая старая» — вопрос, ради
 * которого на дашборд и заходят.
 */
export const CareerDashboardQuery = gql`
  query ErpCareerDashboard {
    careerDashboard {
      members
      trained
      profileReady
      applied
      placed
      vacanciesPublished
      vacanciesDraft
      vacanciesDraftFromAgent
      oldestDraftDays
    }
  }
`;

/**
 * Очередь подтверждения трудоустройства (ТЗ 0069).
 *
 * Студент отметил в своём журнале «вышел на работу» — это ЗАЯВЛЕНИЕ, а не
 * факт: `PlacementRecord` создаёт только сотрудник. Строка выпадает из очереди
 * сама, как только подтверждение записано или студент передумал: очередь
 * ВЫВОДИТСЯ предикатом, а не хранится флагом.
 *
 * Личная заметка студента сюда не приходит — сервер отдаёт её только владельцу.
 */
export const CareerPlacementQueueQuery = gql`
  query CareerPlacementQueue {
    careerPlacementConfirmations {
      id
      studentId
      studentName
      companyName
      position
      appliedAt
      updatedAt
    }
  }
`;

/**
 * Skill reference for the vacancy-form multiselect. Points at the ERP-scoped
 * `careerSkillsErp`, NOT `careerSkills` — the latter is gated on the Student
 * role in the LMS scope, so with an ERP token it raised PermissionDenied and
 * the multiselect rendered silently empty (spec 0034). The operation name is
 * kept distinct from the field.
 */
export const CareerSkillsQuery = gql`
  query ErpCareerSkills($category: String, $query: String) {
    careerSkillsErp(category: $category, query: $query) {
      id
      name
      slug
      category
    }
  }
`;

/**
 * ТЗ 0039 — справочник навыков для вкладки «Навыки».
 *
 * Отдельная операция от `ErpCareerSkills`, а не аргумент к ней: у формы
 * вакансии свой кэш и свой мультиселект, и подмешивать туда счётчики и
 * неактивные строки значило бы менять ей поведение ради соседнего экрана.
 * Счётчики НУЛЛАБЕЛЬНЫ — сервер считает их только при withStats.
 */
export const CareerSkillsDictionaryQuery = gql`
  query ErpCareerSkillsDictionary($query: String, $category: String) {
    careerSkillsErp(
      query: $query
      category: $category
      includeInactive: true
      withStats: true
      limit: 500
    ) {
      id
      name
      slug
      category
      description
      aliases
      levelCriteria {
        level
        points
        example
      }
      iconUrl
      isActive
      holderCount
      courseCount
      demandVacancies
      demandComputedAt
    }
  }
`;

/** ТЗ 0039 — список курсов с числом навыков, левая колонка подраздела. */
export const CareerCourseSkillSummaryQuery = gql`
  query ErpCareerCourseSkillSummary {
    careerCourseSkillSummary {
      courseId
      courseName
      skillCount
      directionTitles
    }
  }
`;

/**
 * ТЗ 0039 — связи «курс ↔ навык».
 *
 * `courseId` НЕОБЯЗАТЕЛЕН: без него сервер отдаёт все связи, и по ним строится
 * обратный взгляд «в каких курсах этот навык». Резолвер это умел с самого
 * начала — блокировал ровно восклицательный знак в этой строке, из-за которого
 * колонка «Курсов: 3» в справочнике оставалась мёртвым числом.
 */
export const CareerCourseSkillsQuery = gql`
  query ErpCareerCourseSkills($courseId: ID) {
    careerCourseSkills(courseId: $courseId) {
      id
      courseId
      courseName
      skillId
      skillName
      skillCategory
      level
      unlockLessonNumber
      order
      isActive
    }
  }
`;

/**
 * ТЗ 0040 — занятия ПРОГРАММЫ курса со сквозным номером.
 *
 * Нужны, чтобы методист выбирал «после занятия №N» по НАЗВАНИЮ. Голый номер
 * он бы всё равно подсмотрел в другом окне, а подсмотрев — ошибся.
 */
export const CourseProgramLessonsQuery = gql`
  query ErpCourseProgramLessons($courseId: ID!) {
    courseProgramLessons(courseId: $courseId) {
      number
      name
      moduleName
      lessonId
      moduleId
      marketingModuleId
      lmsDescription
      taskTypes
    }
    # ТЗ 0040 доводка: рама курса — то, из чего построена его страница на
    # сайте. ERP показывал плоский список занятий и молчал, что рама есть.
    courseProgramFrame(courseId: $courseId) {
      title
      hours
      description
      exists
      marketingModules {
        id
        name
        description
      }
    }
  }
`;

/**
 * ТЗ 0041 — набор навыков ГРУППЫ и её собственные занятия.
 *
 * Прежний `ErpCourseSkillUnlockPreview` («во что номер попадёт в каждой
 * группе») удалён вместе с поводом: набор снимается с курса при создании
 * группы и дальше принадлежит ей, а правка курса действующие группы не
 * догоняет. Вопрос «а что там у соседей» больше не задаётся.
 *
 * Занятие приходит и ссылкой, и номером: ссылка — то, что хранится и чем
 * живёт начисление, номер — подпись «№12», к которой методист привык.
 *
 * `unlockLessonIsOver` сервер отдаёт, но экран его НЕ берёт: это состояние
 * СОХРАНЁННОЙ строки, а плитка «уже открыты» обязана считать черновик — то,
 * что сейчас на экране. Тянуть поле и не показывать значит соврать про
 * намерение; «прошло» видно в подписи занятия в выпадающем списке.
 */
export const GroupSkillsQuery = gql`
  query ErpGroupSkills($groupId: ID!) {
    careerGroupSkills(groupId: $groupId) {
      id
      groupId
      skillId
      skillName
      skillCategory
      level
      unlockLessonId
      unlockLessonNumber
      unlockLessonName
      unlockLessonDate
      order
      isActive
    }
    # Дата и «прошло ли» не украшение: правило на уже прошедшее занятие
    # начислит навык той же ночью, и видеть это надо ДО сохранения.
    groupProgramLessons(groupId: $groupId) {
      lessonId
      number
      name
      moduleName
      date
      isOver
    }
  }
`;

/** ТЗ 0039 — разбор «прошёл / не засчитано» для карточки участника. */
export const CareerStudentCompletionsQuery = gql`
  query ErpCareerStudentCompletions($studentId: ID!) {
    careerStudentCompletions(studentId: $studentId) {
      courseId
      courseName
      groupId
      groupName
      completed
      derived
      hasDiploma
      manualStatus
    }
  }
`;

/**
 * ТЗ 0039 — словарь конструктора анкет.
 *
 * Матрица «тип поля × привязка» приходит с сервера, а не лежит копией здесь:
 * копия разошлась бы с валидацией за месяц, и оператор получал бы отказ на
 * паре, которую сама же форма ему и предложила.
 */
export const ErpCareerExerciseVocabularyQuery = gql`
  query ErpCareerExerciseVocabulary {
    careerExerciseVocabulary {
      maxFields
      maxOptions
      fieldTypes {
        value
        label
        hasOptions
      }
      bindings {
        value
        label
        fieldTypes
        isCollection
        needsLanguage
        subFields
      }
    }
  }
`;

/** ТЗ 0039 шаг 6 — акции. Счётчики штампуются на строки сервером. */
export const CareerPromoCampaignsQuery = gql`
  query ErpCareerPromoCampaigns($includeInactive: Boolean) {
    careerPromoCampaigns(includeInactive: $includeInactive) {
      id
      code
      title
      description
      prefix
      discountKind
      discountValue
      currency
      ttlDays
      hardExpiresAt
      maxCodes
      ruleKind
      ruleParams
      isActive
      issuedCount
      redeemedCount
    }
  }
`;

/** ТЗ 0039 шаг 6 — выданные коды. */
export const CareerPromoCodesQuery = gql`
  query ErpCareerPromoCodes($campaignId: ID, $status: String, $search: String) {
    careerPromoCodes(
      campaignId: $campaignId
      status: $status
      search: $search
    ) {
      id
      code
      status
      studentId
      studentName
      campaignCode
      expiresAt
      redeemedAt
      agreementId
      agreementTitle
      isAmbiguous
      revokeReason
    }
  }
`;

/** ТЗ 0039 доводка — словарь правил для формы акции. */
export const CareerGrowthRulesQuery = gql`
  query ErpCareerGrowthRules {
    careerGrowthRules {
      value
      label
      paramKey
      progressKind
    }
  }
`;

/**
 * ТЗ 0039 доводка — справочник медалей и треков, ТОЛЬКО чтение.
 *
 * Правятся в Django-admin; здесь они нужны, чтобы методист и менеджер вообще
 * понимали, за что студентам что-то начисляется.
 */
export const CareerGrowthDictionaryQuery = gql`
  query ErpCareerGrowthDictionary {
    careerAchievements {
      id
      code
      title
      description
      icon
      category
      points
      ruleKind
      ruleParams
      isSecret
      order
      isActive
      grantedCount
    }
    careerGrowthTracks {
      id
      code
      title
      description
      longDescription
      salaryFrom
      salaryTo
      salaryCurrency
      icon
      order
      isActive
      targetSkills
      targetSkillIds
      siteCourseIds
      steps
      stepRows {
        title
        description
        ruleKind
        ruleParams
      }
      chosenCount
    }
    careerTrackIcons {
      value
      label
    }
  }
`;

/** ТЗ 0039 доводка — тексты постов для вкладки «Акции». */
export const CareerShareTemplatesQuery = gql`
  query ErpCareerShareTemplates {
    careerShareTemplates {
      channel
      language
      text
      targetUrl
      isActive
    }
  }
`;

/**
 * Кого можно добавить в центр (ТЗ 0039, доводка).
 *
 * Один запрос на два режима: с `groupId` — состав группы, с `search` — поиск
 * по всей базе (выпускники старых групп, переведённые). `memberStatus`
 * приходит С СЕРВЕРА: без него оператор узнавал, что человек уже внутри,
 * только из `skipped` в тосте — то есть уже нажав «Добавить».
 *
 * ⚠️ Прежний диалог искал людей через `PaginatedStudents`, чьи роли НЕ
 * включают HR-менеджера и методиста, — а зачислять им разрешено. Поиск молча
 * отдавал пусто. Это поле открыто всем ролям карьерного центра.
 */
export const CareerGroupRosterQuery = gql`
  query ErpCareerGroupRoster($groupId: ID, $search: String) {
    careerGroupRoster(groupId: $groupId, search: $search) {
      studentId
      fullName
      email
      groupName
      memberStatus
    }
  }
`;

/**
 * ТЗ 0040 фаза B — справочник анкет.
 *
 * `lessonTitles`, а не только счётчик: перед правкой методисту нужно знать не
 * «сколько», а «каких именно» уроков это коснётся — иначе он правит вслепую,
 * а замок объясняет отказ уже постфактум.
 */
export const CareerExerciseTemplatesQuery = gql`
  query ErpCareerExerciseTemplates($search: String, $includeHidden: Boolean) {
    careerExerciseTemplates(search: $search, includeHidden: $includeHidden) {
      id
      title
      intro
      outro
      isActive
      hasSubmissions
      submissionsCount
      lessonTitles
      updatedAt
      fields {
        id
        number
        label
        helpText
        placeholder
        fieldType
        required
        options
        maxLength
        binding
        bindingLanguage
      }
    }
  }
`;

/** ТЗ 0040 фаза C — витрина: что школа продаёт. Заводилась сидом. */
export const CareerSiteCoursesQuery = gql`
  query ErpCareerSiteCourses($includeHidden: Boolean) {
    careerSiteCourses(includeHidden: $includeHidden) {
      id
      slug
      name
      order
      isActive
      erpCourseId
      erpCourseName
      skills {
        skillId
        skillName
        level
      }
    }
  }
`;
